import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getBackgroundStyle } from '../../utils/theme';

type CalendarProps = {
  selected?: Date;
  onSelect: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  accentColor?: string;
  textOverAccentColor?: string;
};

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const stripTime = (d: Date) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

export const Calendar = ({
  selected,
  onSelect,
  minDate,
  maxDate,
  accentColor,
  textOverAccentColor
}: CalendarProps) => {
  const [viewDate, setViewDate] = useState(() => selected ?? new Date());

  const today = useMemo(() => stripTime(new Date()), []);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const cells = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;

    const result: { date: Date; isCurrentMonth: boolean }[] = [];

    const prevMonthEnd = new Date(year, month, 0).getDate();
    for (let i = firstDay - 1; i >= 0; i--) {
      result.push({ date: new Date(year, month - 1, prevMonthEnd - i), isCurrentMonth: false });
    }

    for (let d = 1; d <= daysInMonth; d++) {
      result.push({ date: new Date(year, month, d), isCurrentMonth: true });
    }

    for (let d = 1; result.length < totalCells; d++) {
      result.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
    }

    return result;
  }, [year, month]);

  const isDisabled = (date: Date) => {
    const d = stripTime(date);
    if (minDate && d < stripTime(minDate)) return true;
    if (maxDate && d > stripTime(maxDate)) return true;
    return false;
  };

  const handleSelect = (date: Date) => {
    onSelect(date);
    if (date.getMonth() !== month) {
      setViewDate(new Date(date.getFullYear(), date.getMonth(), 1));
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setViewDate(new Date(year, month - 1, 1))}
          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {MONTH_LABELS[month]} {year}
        </span>
        <button
          type="button"
          onClick={() => setViewDate(new Date(year, month + 1, 1))}
          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-0.5">
        {DAY_LABELS.map((day) => (
          <div
            key={day}
            className="text-center text-[11px] font-medium text-gray-400 dark:text-gray-500 py-1"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map(({ date, isCurrentMonth }, i) => {
          const disabled = isDisabled(date);
          const isSelected = selected ? isSameDay(date, selected) : false;
          const isToday = isSameDay(date, today);

          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => handleSelect(date)}
              className={cn(
                'h-8 w-full text-xs rounded-md transition-colors',
                !isCurrentMonth && !isSelected && 'text-gray-300 dark:text-gray-600',
                isCurrentMonth &&
                  !isSelected &&
                  !disabled &&
                  'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800',
                disabled && 'text-gray-300 dark:text-gray-700 cursor-not-allowed',
                isToday &&
                  !isSelected &&
                  isCurrentMonth &&
                  'font-bold ring-1 ring-inset ring-gray-300 dark:ring-gray-600',
                isSelected && 'font-medium rounded-md'
              )}
              style={
                isSelected
                  ? {
                      ...getBackgroundStyle(accentColor ?? '#3b82f6'),
                      color: textOverAccentColor ?? '#ffffff'
                    }
                  : undefined
              }
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};
